<template>
  <div class="p-6 sm:p-8 max-w-5xl mx-auto space-y-8">
    <!-- Header -->
    <header class="text-center space-y-3">
      <h1 class="text-2xl sm:text-3xl font-bold tracking-tight">
        GST/HST Rate Lookup & Invoice
      </h1>

      <div class="header-actions flex justify-center items-center gap-4 space-x-4">
        <div class="inline-flex">
          <BaseButton variant="secondary" class="static" @click="resetForm">Reset</BaseButton>
        </div>
        <div class="inline-flex">
          <BaseButton class="static" :disabled="!canGenerate" @click="downloadPdf">
            Download Invoice (PDF)
          </BaseButton>
        </div>
      </div>
    </header>

    <!-- Status bar -->
    <section
      class="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50
             flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-center"
    >
      <span class="text-sm text-gray-700">Status:</span>

      <template v-if="rate !== null">
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Rate: {{ rate }}%
        </span>
        <span class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          {{ taxTypeLabel }}
        </span>
      </template>

      <span v-else class="text-sm text-gray-500">
        Select Supplier State &amp; Category, then click “Get Rate”.
      </span>
    </section>

    <!-- ========== PHASE 2: Parties + Tax Setup ========== -->
    <div class="grid grid-cols-1 gap-8">
      <!-- Parties -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <h2 class="font-semibold text-lg mb-4 text-left">Parties</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Supplier -->
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium text-gray-900">Supplier</h3>
              <button
                type="button"
                class="text-xs text-indigo-600 hover:underline"
                @click="supplierMultiline = !supplierMultiline"
              >
                {{ supplierMultiline ? 'Single-line' : 'Multiline' }}
              </button>
            </div>

            <!-- Supplier Name -->
            <div class="flex items-center gap-3">
              <label for="supplier-name" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Supplier Name
              </label>
              <div class="flex-1 min-w-0">
                <BaseInput id="supplier-name" v-model="supplier.name" placeholder="Supplier name" class="w-full" />
              </div>
            </div>

            <!-- Supplier GSTIN -->
            <div class="flex items-center gap-3">
              <label for="supplier-gstin" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Supplier GSTIN
              </label>
              <div class="flex-1 min-w-0">
                <BaseInput id="supplier-gstin" v-model="supplier.gstin" placeholder="Supplier GSTIN (optional)" class="w-full" />
              </div>
            </div>

            <!-- Supplier Address -->
            <div class="flex items-start gap-3">
              <label for="supplier-address" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap pt-2">
                Supplier Address
              </label>
              <div class="flex-1 min-w-0">
                <textarea
                  id="supplier-address"
                  v-model="supplier.address"
                  :rows="supplierMultiline ? 4 : 1"
                  :class="[
                    supplierMultiline ? 'h-28 resize-y' : 'h-10 resize-none',
                    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 shadow-sm',
                    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  ]"
                  placeholder="Supplier address (optional)"
                />
              </div>
            </div>

            <!-- Supplier State -->
            <div class="flex items-center gap-3">
              <label for="supplier-state" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Supplier State
              </label>
              <div class="flex-1 min-w-0">
                <SelectState
                  id="supplier-state"
                  v-model="supplier.state"
                  :options="stateOptions"
                  placeholder="Select supplier state"
                  class="w-full"
                />
              </div>
            </div>
          </div>

          <!-- Customer -->
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-medium text-gray-900">Customer</h3>
              <button
                type="button"
                class="text-xs text-indigo-600 hover:underline"
                @click="customerMultiline = !customerMultiline"
              >
                {{ customerMultiline ? 'Single-line' : 'Multiline' }}
              </button>
            </div>

            <!-- Customer Name -->
            <div class="flex items-center gap-3">
              <label for="customer-name" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Customer Name
              </label>
              <div class="flex-1 min-w-0">
                <BaseInput id="customer-name" v-model="customer.name" placeholder="Customer name" class="w-full" />
              </div>
            </div>

            <!-- Customer GSTIN -->
            <div class="flex items-center gap-3">
              <label for="customer-gstin" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Customer GSTIN
              </label>
              <div class="flex-1 min-w-0">
                <BaseInput id="customer-gstin" v-model="customer.gstin" placeholder="Customer GSTIN (optional)" class="w-full" />
              </div>
            </div>

            <!-- Customer Address -->
            <div class="flex items-start gap-3">
              <label for="customer-address" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap pt-2">
                Customer Address
              </label>
              <div class="flex-1 min-w-0">
                <textarea
                  id="customer-address"
                  v-model="customer.address"
                  :rows="customerMultiline ? 4 : 1"
                  :class="[
                    customerMultiline ? 'h-28 resize-y' : 'h-10 resize-none',
                    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 shadow-sm',
                    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                  ]"
                  placeholder="Customer address (optional)"
                />
              </div>
            </div>

            <!-- Customer State -->
            <div class="flex items-center gap-3">
              <label for="customer-state" class="w-48 text-sm font-medium text-gray-700 whitespace-nowrap">
                Customer State
              </label>
              <div class="flex-1 min-w-0">
                <SelectState
                  id="customer-state"
                  v-model="customer.state"
                  :options="stateOptions"
                  placeholder="Select customer state"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Tax setup -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
        <h2 class="font-semibold text-lg mb-4 text-left">Tax Setup</h2>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-6 md:items-end">
          <div class="md:col-span-8 flex flex-col gap-4">
            <SelectCategory
              v-model="category"
              :state="supplier.state"
              label="Product Category"
              placeholder="Select a category"
              :autoSelectFirst="false"
            />
          </div>

          <div class="md:col-span-4">
            <div class="flex items-center gap-3">
              <div class="inline-flex">
                <BaseButton
                  variant="secondary"
                  class="whitespace-nowrap"
                  :disabled="!canGetRate"
                  @click="getRate"
                >
                  Get Rate
                </BaseButton>
              </div>
              <span v-if="rate !== null" class="text-sm text-gray-700">= {{ taxTypeLabel }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseInput  from '@/components/ui/BaseInput.vue'
import SelectState from '@/components/SelectState.vue'
import SelectCategory from '@/components/SelectCategory.vue'
import { useGstRatesStore } from '@/stores/gstRates.js'

const gst = useGstRatesStore()
const stateOptions = computed(() => gst.states.map(s => ({ label: s, value: s })))

const supplier = ref({ name: '', gstin: '', state: '', address: '' })
const customer = ref({ name: '', gstin: '', state: '', address: '' })
const category = ref('')
const rate = ref(null)

const supplierMultiline = ref(false)
const customerMultiline = ref(false)

const sameState = computed(() =>
  supplier.value.state && customer.value.state &&
  supplier.value.state === customer.value.state
)
const taxTypeLabel = computed(() =>
  sameState.value ? 'Intra-state (CGST + SGST)' : 'Inter-state (IGST)'
)
const canGetRate = computed(() => Boolean(supplier.value.state && category.value))

function getRate() {
  if (!canGetRate.value) return
  const r = gst.rateFor(supplier.value.state, category.value)
  rate.value = r == null ? null : Number(r)
}

const canGenerate = computed(() => false)

function resetForm() {
  supplier.value = { name: '', gstin: '', state: '', address: '' }
  customer.value = { name: '', gstin: '', state: '', address: '' }
  category.value = ''
  rate.value = null
}

function downloadPdf() {
  console.log('[GSTInvoiceView] downloadPdf() - Phase 2 (disabled)')
}
</script>

<style scoped>
:deep(.header-actions){
  display:flex !important;
  justify-content:center !important;
  flex-wrap:nowrap !important;
  gap:1rem !important;
}
.header-actions > * + * { margin-left: 1rem; }
:deep(.base-button){
  position: static !important;
  display: inline-flex !important;
}
</style>
